import { useEffect, useState } from "react";
import { api } from "../../../lib/api";

export interface Company {
  id: number;
  name: string;
  short_code: string;
  default_language: "en" | "th";
  currency: string;
  reply_to_email: string | null;
}

const LS_KEY = "pc_company_id";

export function useCurrentCompany() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, _setCompanyId] = useState<number | null>(() => {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    api<{ companies: Company[] }>("/api/collection/companies").then((r) => {
      setCompanies(r.companies);
      if (!r.companies.length) return;
      // Fall back to the first company when nothing is stored — and also when the stored id
      // is no longer offered. A company that has since been hidden would otherwise leave the
      // browser pinned to a selection the app can't resolve, and every page empty.
      const storedIsValid = companyId != null && r.companies.some((c) => c.id === companyId);
      if (!storedIsValid) {
        _setCompanyId(r.companies[0].id);
        localStorage.setItem(LS_KEY, String(r.companies[0].id));
      }
    }).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  function setCompanyId(id: number) {
    _setCompanyId(id);
    localStorage.setItem(LS_KEY, String(id));
  }

  const current = companies.find((c) => c.id === companyId) || null;
  return { companies, current, companyId, setCompanyId };
}

export default function CompanySwitcher() {
  const { companies, current, companyId, setCompanyId } = useCurrentCompany();
  if (!companies.length) return null;

  const label = (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      Acting as
    </span>
  );

  // With a single company there is nothing to switch to, so a dropdown is just a control
  // that can't do anything. Show the name as text and let the header stay quiet.
  if (companies.length === 1) {
    return (
      <div className="flex items-baseline gap-2">
        {label}
        <span className="text-sm font-semibold tracking-tightish text-slate-900">
          {(current ?? companies[0]).name}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {label}
      <div className="relative">
        {/* No fixed width — the native select sizes itself to the longest company name. */}
        <select
          value={companyId ?? ""}
          onChange={(e) => setCompanyId(Number(e.target.value))}
          className="cursor-pointer appearance-none !border-slate-200 !py-1.5 !pl-2.5 pr-8 text-sm font-semibold !text-slate-900 hover:!bg-slate-50"
          aria-label="Active company"
        >
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <svg className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M10 13a.75.75 0 01-.53-.22l-3.75-3.75a.75.75 0 111.06-1.06L10 11.19l3.22-3.22a.75.75 0 111.06 1.06l-3.75 3.75A.75.75 0 0110 13z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
}
